import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Plus, Link, X } from "lucide-react";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";
import * as api from "../../lib/api";

export function SoftwareInstallForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("low");
    const [software, setSoftware] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedSoftware, setSelectedSoftware] = useState([]);
    const [showCustom, setShowCustom] = useState(false);
    const [customName, setCustomName] = useState("");
    const [customUrl, setCustomUrl] = useState("");
    const [targetMachine, setTargetMachine] = useState("");
    const [urgencyReason, setUrgencyReason] = useState("");

    useEffect(() => {
        loadSoftware();
    }, []);

    const loadSoftware = async () => {
        try {
            const [swData, catData] = await Promise.all([
                api.getSoftwareCatalog(),
                api.getSoftwareCategories(),
            ]);
            setSoftware(swData);
            setCategories(catData);
        } catch (error) {
            console.error("Failed to load software:", error);
        }
    };

    const toggleSoftware = (sw) => {
        setSelectedSoftware((prev) => {
            const exists = prev.find((s) => s.software_id === sw.id);
            if (exists) {
                return prev.filter((s) => s.software_id !== sw.id);
            } else {
                return [
                    ...prev,
                    {
                        software_id: sw.id,
                        software_name: sw.name,
                        chocolatey_id: sw.chocolatey_id,
                        version: sw.version,
                    },
                ];
            }
        });
    };

    const addCustomSoftware = () => {
        if (!customName.trim()) return;
        setSelectedSoftware((prev) => [
            ...prev,
            {
                software_id: `custom-${Date.now()}`,
                software_name: customName.trim(),
                is_custom: true,
                download_url: customUrl.trim() || null,
                version: null,
            },
        ]);
        setCustomName("");
        setCustomUrl("");
        setShowCustom(false);
    };

    const removeCustomSoftware = (id) => {
        setSelectedSoftware((prev) => prev.filter((s) => s.software_id !== id));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const softwareNames = selectedSoftware.map((s) => s.software_name).join(", ");
        onSubmit({
            title: baseForm.title || `Installation logiciel - ${softwareNames}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                software_items: selectedSoftware,
                target_machine: targetMachine,
                urgency_reason: urgencyReason,
            },
        });
    };

    const customItems = selectedSoftware.filter((s) => s.is_custom);

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Installation Adobe Creative Suite"
                descriptionPlaceholder="Raison de la demande..."
            >
                <div className="form-group">
                    <Label className="form-label">Logiciels à installer</Label>
                    <div className="border rounded-md p-3 space-y-3">
                        {(() => {
                            const catMap = {};
                            categories.forEach((c) => { catMap[c.id] = c.label; });
                            const grouped = {};
                            software.forEach((sw) => {
                                const cat = sw.category || "autre";
                                if (!grouped[cat]) grouped[cat] = [];
                                grouped[cat].push(sw);
                            });
                            const cats = categories.map((c) => c.id).filter((c) => grouped[c]?.length);
                            return cats.map((cat) => (
                                <div key={cat}>
                                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1 px-1">
                                        {catMap[cat] || cat}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-2">
                                        {grouped[cat].map((sw) => (
                                            <label
                                                key={sw.id}
                                                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                            >
                                                <Checkbox
                                                    checked={selectedSoftware.some((s) => s.software_id === sw.id)}
                                                    onCheckedChange={() => toggleSoftware(sw)}
                                                    data-testid={`software-${sw.id}`}
                                                />
                                                <div>
                                                    <div className="text-sm font-medium">{sw.name}</div>
                                                    <div className="text-xs text-gray-500">v{sw.version}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ));
                        })()}

                        {/* Custom software items already added */}
                        {customItems.length > 0 && (
                            <div className="border-t pt-3 space-y-2">
                                {customItems.map((item) => (
                                    <div
                                        key={item.software_id}
                                        className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-100 rounded-md"
                                        data-testid={`custom-sw-${item.software_id}`}
                                    >
                                        <Checkbox checked readOnly className="pointer-events-none" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium">{item.software_name}</div>
                                            {item.download_url && (
                                                <a
                                                    href={item.download_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 truncate"
                                                >
                                                    <Link className="w-3 h-3 flex-shrink-0" />
                                                    {item.download_url}
                                                </a>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeCustomSoftware(item.software_id)}
                                            className="text-gray-400 hover:text-red-500 p-1"
                                            data-testid={`remove-custom-sw-${item.software_id}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* "Autre" button and inline form */}
                        <div className="border-t pt-3">
                            {!showCustom ? (
                                <button
                                    type="button"
                                    onClick={() => setShowCustom(true)}
                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors"
                                    data-testid="add-custom-software-btn"
                                >
                                    <Plus className="w-4 h-4" />
                                    Autre logiciel (hors catalogue)
                                </button>
                            ) : (
                                <div className="space-y-3 bg-gray-50 rounded-lg p-3" data-testid="custom-software-form">
                                    <div>
                                        <Label className="text-xs font-medium text-gray-600">Nom du logiciel</Label>
                                        <Input
                                            value={customName}
                                            onChange={(e) => setCustomName(e.target.value)}
                                            placeholder="Ex: Blender, Figma, ..."
                                            autoFocus
                                            data-testid="custom-software-name"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-medium text-gray-600">
                                            Lien de téléchargement (optionnel)
                                        </Label>
                                        <Input
                                            value={customUrl}
                                            onChange={(e) => setCustomUrl(e.target.value)}
                                            placeholder="https://www.example.com/download"
                                            data-testid="custom-software-url"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={addCustomSoftware}
                                            disabled={!customName.trim()}
                                            data-testid="confirm-custom-software-btn"
                                        >
                                            Ajouter
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => { setShowCustom(false); setCustomName(""); setCustomUrl(""); }}
                                        >
                                            Annuler
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Machine cible</Label>
                    <Input
                        value={targetMachine}
                        onChange={(e) => setTargetMachine(e.target.value)}
                        placeholder="Nom de la machine ou numéro d'inventaire"
                        data-testid="target-machine"
                    />
                </div>

                <div className="form-group">
                    <Label className="form-label">Raison d'urgence (optionnel)</Label>
                    <Textarea
                        value={urgencyReason}
                        onChange={(e) => setUrgencyReason(e.target.value)}
                        placeholder="Si urgent, expliquez pourquoi..."
                        data-testid="urgency-reason"
                    />
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
